import { i18n } from '../i18n/i18n'

import React, { Component } from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import alertService from '../services/alert.service'
import storageService from '../services/storage.service'
import subscriptionApi, {
  SubscriptionPackage,
  PreviewPlanSubscription,
  ApiPrintUsageSummary,
  ApiPrintUsageLabel
} from '../apis/subscriptionApi'
import { Elements } from '@stripe/react-stripe-js'
import { Stripe, loadStripe } from '@stripe/stripe-js'
import Card from '../components/Card'
import CardButton from '../components/CardButton'
import PageHeading from '../components/PageHeading'
import Row from '../components/Row/Row'
import Heading from '../components/Heading'
import SubscriptionSelector from '../components/SubscriptionSelector/SubscriptionSelector'
import InjectedCheckoutForm from '../partials/PaymentForm'
import authService from '../services/auth.service'
import LoadingOverlay from '../components/LoadingOverlay/LoadingOverlay'
import { ConfirmParams } from '../components/Confirm/Confirm.state'
import confirmService from '../services/confirm.service'
import Icon from '../components/Icon'
import DateRangePicker from '../components/DateRangePicker'

interface Props extends RouteComponentProps {
}

type UsageSortKey = 'labelName' | 'printsCount' | 'share' | 'lastPrintedAt'
type SortDirection = 'asc' | 'desc'

interface State {
    isTrial: boolean
    mainPage: boolean
    paymentPage: boolean
    subscriptionPackages: SubscriptionPackage[]
    amountSelected: number,
    selectedSubscriptionId: number
    stripe: Stripe | null
    clientSecret: string
    isLoading: boolean
    currentPrice: string
    currentMembershipName: string
    status: string,
    currentPlan: SubscriptionPackage | null,
    selectedPlan: SubscriptionPackage | null
    userId: number | string | null
    isUpgrade: boolean
    newPurchasePlan: PreviewPlanSubscription | null
    hasSubscription: boolean
    usageSummary: ApiPrintUsageSummary | null
    usageStartAt: string
    usageEndAt: string
    isUsageLoading: boolean
    isUsageCollapsed: boolean
    usageSortBy: UsageSortKey
    usageSortDirection: SortDirection
}

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || '')

const startOfCurrentMonth = () => {
  const date = new Date()
  return new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0).toISOString()
}

const endOfCurrentMonth = () => {
  const date = new Date()
  return new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999).toISOString()
}

class UserSubscriptionLayout extends Component<Props, State> {
  initialState: State = {
    isTrial: false,
    mainPage: true,
    paymentPage: false,
    subscriptionPackages: [],
    amountSelected: 0,
    selectedSubscriptionId: 0,
    stripe: null,
    clientSecret: '',
    isLoading: false,
    currentPrice: '',
    currentMembershipName: '',
    status: '',
    currentPlan: null,
    selectedPlan: null,
    userId: null,
    isUpgrade: false,
    newPurchasePlan: null,
    hasSubscription: false,
    usageSummary: null,
    usageStartAt: startOfCurrentMonth(),
    usageEndAt: endOfCurrentMonth(),
    isUsageLoading: false,
    isUsageCollapsed: false,
    usageSortBy: 'printsCount',
    usageSortDirection: 'desc',
  }

  constructor (props: Props) {
    super(props)
    this.state = this.initialState
    this.handleSubscribeClick = this.handleSubscribeClick.bind(this)
    this.handleSelectSubscription = this.handleSelectSubscription.bind(this)
    this.handleCancelSubscription = this.handleCancelSubscription.bind(this)
    this.doCancel = this.doCancel.bind(this)
    this.handleDowngradeClick = this.handleDowngradeClick.bind(this)
    this.doDowngrade = this.doDowngrade.bind(this)
    this.handleUpgradeSubscription = this.handleUpgradeSubscription.bind(this)
    this.doUpgrade = this.doUpgrade.bind(this)
    this.handleReactivateSubscription = this.handleReactivateSubscription.bind(this)
    this.doReactivate = this.doReactivate.bind(this)
  }

  componentDidMount (): void {
    stripePromise.then(stripe => {
      this.setState({ stripe })
    })

    this.fetchMembershipData()
    this.fetchApiPrintUsageSummary()
  }

    handlePaymentSuccess = () => {
    this.setState({ mainPage: true, paymentPage: false })
    this.fetchMembershipData()
  }

  fetchMembershipData () {
    const authData = authService.tokenData()
    if (!authData) return
    const { id } = authData

    subscriptionApi.getActiveMembership(id, membership => {
      if (membership?.type == 'trial') {
        subscriptionApi.list(id, subscriptions => {
          if (!subscriptions) return
          this.setState({
            isTrial: true,
            subscriptionPackages: subscriptions.plans,
            currentPlan: subscriptions.current_plan,
            mainPage: true,
            status: membership?.status ?? '',
            hasSubscription: subscriptions.has_subscription
          })
          storageService.set('userPlan', subscriptions.current_plan.slug)
        })
      } else {
        this.setState({
          isTrial: false,
          mainPage: true,
          currentPrice: membership?.price ?? '',
          currentMembershipName: membership?.name ?? '',
          status: membership?.status ?? ''
        })
      }
    })
  }

  fetchApiPrintUsageSummary () {
    const authData = authService.tokenData()
    if (!authData) return

    const { usageStartAt, usageEndAt } = this.state

    this.setState({ isUsageLoading: true })
    subscriptionApi.getApiPrintUsageSummary({
      userId: authData.id,
      startAt: usageStartAt,
      endAt: usageEndAt
    }, usageSummary => {
      this.setState({
        isUsageLoading: false,
        usageSummary
      })
    })
  }

  handleUsageDateChange = (usageStartAt: string, usageEndAt: string) => {
    this.setState({ usageStartAt, usageEndAt })
  }

  handleUsageSearch = () => {
    this.fetchApiPrintUsageSummary()
  }

  handleUsageReset = () => {
    this.setState({
      usageStartAt: startOfCurrentMonth(),
      usageEndAt: endOfCurrentMonth()
    }, () => this.fetchApiPrintUsageSummary())
  }

  handleUsageToggle = () => {
    this.setState(state => ({ isUsageCollapsed: !state.isUsageCollapsed }))
  }

  handleUsageSort = (usageSortBy: UsageSortKey) => {
    this.setState(state => ({
      usageSortBy,
      usageSortDirection: state.usageSortBy === usageSortBy && state.usageSortDirection === 'asc' ? 'desc' : 'asc'
    }))
  }

  fetchPreviewPlanSubscription (isUpgrade: boolean, planId?: number) {
    const authData = authService.tokenData()
    if (!authData) return
    const { id } = authData

    const payload = {
      package_id: planId || this.state.selectedSubscriptionId,
      userId: id,
      isUpgrade
    }

    subscriptionApi.getPreviewPlanSubscription(payload, previewPlanSubscription => {
      if (!previewPlanSubscription) return
      this.setState({
        newPurchasePlan: previewPlanSubscription
      })
    })
  }

  handleSubscribeClick () {
    this.setState({ isLoading: true })
    const authData = authService.tokenData()
    if (!authData) return
    if (this.state.selectedSubscriptionId == 0) {
      alertService.warning(i18n('Please select a subscription plan'))
      this.setState({ isLoading: false })
      return
    }
    const userId = authData.id

    this.setState({ isLoading: false })
    this.setState({
      mainPage: false,
      paymentPage: true,
      isLoading: false,
      userId
    })
  }

  handleSelectSubscription = (subscriptionId: number, amount: number) => {
    if (!subscriptionId) return
    const selectedPlan = this.state.subscriptionPackages.find(s => s.id === subscriptionId) || null

    this.setState({
      selectedSubscriptionId: subscriptionId,
      amountSelected: amount,
      selectedPlan
    })
  }

  handleCancelSubscription () {
    const authData = authService.tokenData()
    if (!authData) return
    const { id } = authData

    const confirm: ConfirmParams = {
      id,
      type: 'warning',
      question: i18n('Are you sure you want to cancel your subscription to QPRO+?'),
      confirmText: i18n('Yes'),
      cancelText: i18n('No'),
      onConfirm: () => this.doCancel(id)
    }
    confirmService.push(confirm)
  }

  doCancel (id: number) {
    this.setState({ isLoading: true })
    subscriptionApi.cancelSubscription(id, canceledSubscription => {
      this.setState({ isLoading: false })
      if (!canceledSubscription) return

      alertService.success(i18n('Membership canceled correctly. A confirmation e-mail has been sent.'))
      this.fetchMembershipData()

      // // Log out user
      // storageService.remove('authToken')
      // this.props.history.push(routes.signIn)
    })
  }

  handleDowngradeClick (planId: number) {
    const authData = authService.tokenData()
    if (!authData) return
    const { id } = authData

    this.setState({
      selectedSubscriptionId: planId
    })

    this.fetchPreviewPlanSubscription(false, planId)

    const confirm: ConfirmParams = {
      id,
      type: 'warning',
      question: this.getConfirmQuestion('downgrade'),
      confirmText: i18n('Yes'),
      cancelText: i18n('No'),
      onConfirm: () => this.doDowngrade(id)
    }
    confirmService.push(confirm)
  }

  doDowngrade (id: number) {
    this.setState({ isLoading: true })
    const payload = {
      userId: id,
      package_id: this.state.selectedSubscriptionId
    }
    subscriptionApi.downgradeSubscription(payload, downgradedSubscription => {
      this.setState({ isLoading: false })
      if (!downgradedSubscription) return
      alertService.success(i18n('Downgraded successfully'))
      this.setState({ isUpgrade: true })
      this.fetchMembershipData()
    })
  }

  handleUpgradeSubscription (planId: number) {
    const authData = authService.tokenData()
    if (!authData) return
    const { id } = authData

    this.setState({
      selectedSubscriptionId: planId // Set the planId
    })

    this.fetchPreviewPlanSubscription(true, planId)

    const confirm: ConfirmParams = {
      id,
      type: 'warning',
      question: this.getConfirmQuestion('upgrade'),
      confirmText: i18n('Yes'),
      cancelText: i18n('No'),
      onConfirm: () => this.doUpgrade(id)
    }
    confirmService.push(confirm)
  }

  doUpgrade (id: number) {
    this.setState({ isLoading: true })
    const payload = {
      package_id: this.state.selectedSubscriptionId,
      userId: id
    }

    subscriptionApi.upgradeSubscription(payload, upgradedSubscription => {
      this.setState({ isLoading: false })
      if (!upgradedSubscription) return
      alertService.success(i18n('Membership upgraded successfully.'))
      this.setState({ isUpgrade: true })
      this.fetchMembershipData()
    })
  }

  handleReactivateSubscription () {
    const authData = authService.tokenData()
    if (!authData) return
    const { id } = authData

    const confirm: ConfirmParams = {
      id,
      type: 'warning',
      question: i18n('Are you sure you want to Reactivate your subscription to QPRO+?'),
      confirmText: i18n('Yes'),
      cancelText: i18n('No'),
      onConfirm: () => this.doReactivate(id)
    }
    confirmService.push(confirm)
  }

  doReactivate (id: number) {
    this.setState({ isLoading: true })
    subscriptionApi.reactivateSubscription(id, reactivatedSubscription => {
      this.setState({ isLoading: false })
      if (!reactivatedSubscription) return
      alertService.success(i18n('Membership Reactivated successfully.'))
      this.fetchMembershipData()
    })
  }

  getConfirmQuestion (type: 'upgrade' | 'downgrade') {
    const { newPurchasePlan } = this.state

    const actionText =
            type === 'upgrade'
              ? i18n('upgrade your subscription to QPRO+?')
              : i18n('downgrade your subscription to QPRO+?')

    const effectText =
            type === 'upgrade'
              ? i18n('The plan upgrade will take effect immediately.')
              : i18n('The plan downgrade will take effect at your next billing cycle.')

    // No preview info available
    if (!newPurchasePlan) {
      return i18n('{effect}\nAre you sure you want to {action}?', {
        effect: effectText,
        action: actionText
      })
    }
    return i18n(
      '{effect}\n\nNext Billing Amount: {amount}\nEffective On: {date}\n\nAre you sure you want to {action}?',
      {
        effect: effectText,
        amount: `$${newPurchasePlan.next_cycle_total}`,
        date: newPurchasePlan.effective_on,
        action: actionText
      }
    )
  }

  formatUsageDate (date?: string | null) {
    if (!date) return i18n('Never')

    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(new Date(date))
  }

  getUsageShare (printsCount: number) {
    const totalPrints = this.state.usageSummary?.totalPrints || 0
    if (!totalPrints) return '0%'
    return `${Math.round((Number(printsCount) / totalPrints) * 100)}%`
  }

  getUsageSortValue (label: ApiPrintUsageLabel, sortBy: UsageSortKey) {
    switch (sortBy) {
      case 'labelName':
        return label.labelName.toLowerCase()
      case 'lastPrintedAt':
        return label.lastPrintedAt ? new Date(label.lastPrintedAt).getTime() : 0
      case 'share':
      case 'printsCount':
      default:
        return Number(label.printsCount)
    }
  }

  getSortedUsageLabels () {
    const labels = this.state.usageSummary?.labels || []
    const { usageSortBy, usageSortDirection } = this.state

    return [...labels].sort((a, b) => {
      const first = this.getUsageSortValue(a, usageSortBy)
      const second = this.getUsageSortValue(b, usageSortBy)
      const direction = usageSortDirection === 'asc' ? 1 : -1

      if (typeof first === 'string' && typeof second === 'string') {
        return first.localeCompare(second) * direction
      }

      return (Number(first) - Number(second)) * direction
    })
  }

  getUsageSortIcon (sortBy: UsageSortKey) {
    if (this.state.usageSortBy !== sortBy) return 'arrow-up-down'
    return this.state.usageSortDirection === 'asc' ? 'arrow-up' : 'arrow-down'
  }

  getUsageHeader (text: string, sortBy: UsageSortKey, align: 'left' | 'right' = 'left') {
    return (
            <button
                type="button"
                className={`flex items-center gap-1 w-full text-gray-500 hover:text-gray-900 focus:outline-none ${align === 'right' ? 'justify-end' : 'justify-start'}`}
                onClick={() => this.handleUsageSort(sortBy)}>
                <span>{text}</span>
                <Icon
                    name={this.getUsageSortIcon(sortBy)}
                    size="extraSmall"
                    color="inherit"/>
            </button>
    )
  }

  handleUsagePrintPdf = () => {
    const authData = authService.tokenData()
    if (!authData) return

    const { usageStartAt, usageEndAt, usageSortBy, usageSortDirection } = this.state
    const pdfUrl = subscriptionApi.getApiPrintUsageSummaryPdfUrl({
      userId: authData.id,
      startAt: usageStartAt,
      endAt: usageEndAt,
      sortBy: usageSortBy,
      sortDirection: usageSortDirection
    })

    window.open(pdfUrl, '_blank')
  }

  getApiUsageSummary () {
    const { usageSummary, usageStartAt, usageEndAt, isUsageLoading, isUsageCollapsed } = this.state
    const labels = this.getSortedUsageLabels()

    return (
            <div className="mt-8 border border-gray-300 border-solid bg-white shadow-sm">
                <button
                    type="button"
                    className="w-full flex items-center justify-between gap-3 p-4 text-left focus:outline-none"
                    onClick={this.handleUsageToggle}>
                    <div>
                        <h3 className="mb-1 text-base font-medium text-gray-900">
                            {i18n('Usage Summary')}
                        </h3>
                        <p className="text-sm text-gray-500">
                            {i18n('Total print usage for the selected date range.')}
                        </p>
                    </div>
                    <span className="flex items-center gap-2 border border-gray-300 border-solid px-3 py-2 text-sm font-medium text-gray-600 bg-white shrink-0">
                        <Icon
                            name={isUsageCollapsed ? 'chevron-down' : 'chevron-up'}
                            size="small"
                            color="gray"/>
                        <span>{isUsageCollapsed ? i18n('Show') : i18n('Hide')}</span>
                    </span>
                </button>

                {!isUsageCollapsed && (
                    <div className="border-t border-gray-200 p-4">
                        <div className="grid grid-cols-1 md:grid-cols-[minmax(260px,1fr)_110px_110px] gap-3 items-start mb-4">
                            <DateRangePicker
                                startAt={usageStartAt}
                                endAt={usageEndAt}
                                onChange={this.handleUsageDateChange}/>
                            <button
                                className="bg-primary text-white capitalize focus:outline-none block py-2 px-5 h-input mb-4"
                                onClick={this.handleUsageSearch}>
                                {i18n('Search')}
                            </button>
                            <button
                                className="bg-primary-light text-white capitalize focus:outline-none block py-2 px-5 h-input mb-4"
                                onClick={this.handleUsageReset}>
                                {i18n('Reset')}
                            </button>
                        </div>
                        <div className="flex justify-end mb-4">
                            <button
                                type="button"
                                className="bg-primary text-white capitalize focus:outline-none flex items-center gap-2 py-2 px-5 h-input"
                                onClick={this.handleUsagePrintPdf}>
                                <Icon name="printer" size="small" color="white"/>
                                {i18n('Print PDF')}
                            </button>
                        </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="border border-gray-300 border-solid bg-white p-4 shadow-sm">
                        <p className="text-xs uppercase text-gray-500 mb-1">{i18n('Total Print Events')}</p>
                        <p className="text-2xl font-semibold text-gray-900">
                            {isUsageLoading ? i18n('Loading') : Number(usageSummary?.totalPrints || 0).toLocaleString()}
                        </p>
                    </div>
                    <div className="border border-gray-300 border-solid bg-white p-4 shadow-sm">
                        <p className="text-xs uppercase text-gray-500 mb-1">{i18n('Labels Used')}</p>
                        <p className="text-2xl font-semibold text-gray-900">
                            {isUsageLoading ? i18n('Loading') : Number(usageSummary?.labelsCount || 0).toLocaleString()}
                        </p>
                    </div>
                    <div className="border border-gray-300 border-solid bg-white p-4 shadow-sm">
                        <p className="text-xs uppercase text-gray-500 mb-1">{i18n('Date Range')}</p>
                        <p className="text-sm font-medium text-gray-900">
                            {this.formatUsageDate(usageStartAt)} - {this.formatUsageDate(usageEndAt)}
                        </p>
                    </div>
                </div>

                <div className="border border-gray-300 border-solid bg-white shadow-sm overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                        <tr className="border-b border-gray-200 text-left text-gray-500">
                            <th className="py-3 px-4 font-medium">{this.getUsageHeader(i18n('Label'), 'labelName')}</th>
                            <th className="py-3 px-4 font-medium text-right">{this.getUsageHeader(i18n('Print Events'), 'printsCount', 'right')}</th>
                            <th className="py-3 px-4 font-medium text-right">{this.getUsageHeader(i18n('Share'), 'share', 'right')}</th>
                            <th className="py-3 px-4 font-medium">{this.getUsageHeader(i18n('Last Used'), 'lastPrintedAt')}</th>
                        </tr>
                        </thead>
                        <tbody>
                        {labels.length === 0 && (
                            <tr>
                                <td className="py-5 px-4 text-center text-gray-500" colSpan={4}>
                                    {isUsageLoading ? i18n('Loading usage summary.') : i18n('No print usage found for this date range.')}
                                </td>
                            </tr>
                        )}
                        {labels.map(label => (
                            <tr key={label.customLabelId} className="border-b border-gray-100 last:border-b-0">
                                <td className="py-3 px-4 text-gray-900">{label.labelName}</td>
                                <td className="py-3 px-4 text-right font-medium text-gray-900">
                                    {Number(label.printsCount).toLocaleString()}
                                </td>
                                <td className="py-3 px-4 text-right text-gray-600">
                                    {this.getUsageShare(Number(label.printsCount))}
                                </td>
                                <td className="py-3 px-4 text-gray-600">
                                    {this.formatUsageDate(label.lastPrintedAt)}
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
                    </div>
                )}
            </div>
    )
  }

  getTrialScreen () {
    const { isTrial, mainPage, currentPlan, isLoading, hasSubscription } = this.state

    if (isTrial && mainPage) {
      return (
                <div className='flex justify-between mb-5'>
                    {isLoading && <LoadingOverlay/>}
                    <div>
                        <Heading
                            text={i18n('QPRO+ Current plan was {plan}', {
                              plan: currentPlan && currentPlan.slug !== 'free'
                                ? currentPlan.slug.charAt(0).toUpperCase() + currentPlan.slug.slice(1)
                                : i18n('Free')
                            })}

                            className="text-theme"
                        />

                        <div className="text-secondary">
                            {currentPlan && currentPlan.slug !== 'free'
                              ? (<>{i18n('You are currently on the {plan} plan.', { plan: currentPlan.slug.charAt(0).toUpperCase() + currentPlan.slug.slice(1) })}</>)
                              : (<>{i18n('You have no active plans.')}<br/>{i18n('You are running in a limited version.')}</>)}
                            <br/>
                            <div
                                className="max-w-md border border-gray-300 border-solid bg-white p-5 mt-5 shadow-sm">
                                <h3 className="mb-4 text-base font-medium text-gray-900">
                                    {i18n('Subscription Details')}
                                </h3>

                                <div className="space-y-3 text-sm">
                                    <div className="flex gap-2">
    <span className="text-gray-500">
      {i18n('Subscriber Name')}:
    </span>
                                        <span className="font-medium text-gray-800">
      {currentPlan?.subcsriber_name}
    </span>
                                    </div>

                                    <div className="flex gap-2">
    <span className="text-gray-500">
      {i18n('Account Email')}:
    </span>
                                        <span className="font-medium text-gray-800">
      {currentPlan?.email}
    </span>
                                    </div>

                                    <div className="flex gap-2">
    <span className="text-gray-500">
      {i18n('Renewal Date')}:
    </span>
                                        <span className="font-medium text-gray-800">
      {currentPlan?.renewal_date}
    </span>
                                    </div>
                                </div>

                            </div>

                        </div>
                        <br/><br/>
                        <Row column={1}>
                            <SubscriptionSelector
                                subscriptions={this.state.subscriptionPackages}
                                onSelectSubscription={this.handleSelectSubscription}
                                selectedSubscriptionId={this.state.selectedSubscriptionId}
                                onDowngrade={(planId) => this.handleDowngradeClick(planId)}
                                onUpgrade={(planId) => this.handleUpgradeSubscription(planId)}
                                currentPurchasePlan={currentPlan}
                                handleReactivateSubscription={this.handleReactivateSubscription}
                                handleCancelSubscription={this.handleCancelSubscription}
                            /><br/><br/>
                        </Row>
                        {this.getApiUsageSummary()}
                        {currentPlan && currentPlan.slug === 'free' && (
                            <Row column={1}>
                                <CardButton
                                    proceedText={i18n('Subscribe')}
                                    onProceedClick={() => {
                                      const { selectedSubscriptionId } = this.state

                                      if (!selectedSubscriptionId) {
                                        alertService.warning(i18n('Please select a subscription plan'))
                                        return
                                      }

                                      if (
                                        currentPlan &&
                                            currentPlan.slug === 'free' &&
                                            selectedSubscriptionId === currentPlan.id
                                      ) {
                                        alertService.warning(i18n('Please select a paid subscription plan'))
                                        return
                                      }

                                      if (hasSubscription) {
                                        this.handleUpgradeSubscription(this.state.selectedSubscriptionId)
                                        return
                                      }

                                      this.handleSubscribeClick()
                                    }}
                                />
                            </Row>
                        )}
                    </div>
                </div>
      )
    }
  }

  getPaymentScreen () {
    if (this.state.paymentPage) {
      const { selectedPlan, userId } = this.state

      return (
                <div>
                    <Elements stripe={stripePromise}>
                        <InjectedCheckoutForm
                            selectedPlan={selectedPlan}
                            userId={userId}
                            onPaymentSuccess={this.handlePaymentSuccess}
                        />
                    </Elements>
                </div>
      )
    }
  }

  getCanceledScren () {
    if (!this.state.isTrial && this.state.mainPage && this.state.status === 'pendingCancel') {
      let { currentPrice, currentMembershipName } = this.state
      currentMembershipName = 'QPRO+ ' + currentMembershipName
      return (
                <div className='flex justify-between mb-5'>
                    {this.state.isLoading && <LoadingOverlay/>}
                    <div>
                        <Heading
                            text={currentMembershipName}
                            className='text-theme'/>
                        <div className="text-secondary">
                            {i18n('Your subscription has been canceled.')}
                            <br/>
                        </div>
                        <br/><br/>
                    </div>
                </div>
      )
    }
  }

  getMembershipScreen () {
    if (!this.state.isTrial && this.state.mainPage && this.state.status === 'active') {
      let { currentPrice, currentMembershipName } = this.state
      currentMembershipName = 'QPRO+ ' + currentMembershipName
      return (
                <div className='flex justify-between mb-5'>
                    {this.state.isLoading && <LoadingOverlay/>}
                    <div>
                        <Heading
                            text={currentMembershipName}
                            className='text-theme'/>
                        <div className="text-secondary">
                            Your current plan is a paid monthly subscription.<br/>The monthly payment amount is
                            ${currentPrice}.
                            <br/>
                        </div>
                        <br/><br/>
                        <Row column={1}>
                            <CardButton
                                proceedText={i18n('Cancel subscription')}
                                onProceedClick={this.handleCancelSubscription}/>
                        </Row>
                    </div>
                </div>
      )
    }
  }

  render () {
    return (

            <Card>
                <PageHeading text={i18n('My subscription')}/>
                {!this.state.mainPage && (
                    <div className="flex justify-between gap-2 mt-5">
                        <button
                            className="flex items-center bg-blue-600 text-white px-4 py-2"
                            onClick={this.handlePaymentSuccess}
                        >
                            <Icon
                                name="arrow-left"
                                size="small"
                                color="white"
                                className="mr-2"
                            />
                            {i18n('Back')}
                        </button>
                    </div>
                )}
                {this.getMembershipScreen()}
                {this.getTrialScreen()}
                {this.getPaymentScreen()}
                {this.getCanceledScren()}
            </Card>

    )
  }
}

export default withRouter(UserSubscriptionLayout)
